import {
    StyleFunction,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQApprovalInput,
    useApprovalLazyQuery,
    useApprovalSaveMutation,
} from "graphql-sdk";
import { schemaApproval } from "lib";
import { useTranslation } from "locales";
import React, { useCallback, useEffect } from "react";
import { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks";
import { RootStackScreenProps } from "../../../../navigation";
import { AdminContainer } from "../../../container";
import { Form } from "../form";

type ScreenProps = RootStackScreenProps<"APPROVAL_EDIT">;

export function ApprovalEditScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.approval");

    const [t] = useTranslation();

    const [approvalSave] = useApprovalSaveMutation();
    const { handleMutationError } = useHandleMutationError();

    const [getApproval, approval] = useApprovalLazyQuery({
        fetchPolicy: "no-cache",
    });
    const form = useForm<GQApprovalInput>(schemaApproval, null);

    const [{ values }, { setDefaultValues }] = form;

    const styles = useThemedStyle(styleFunc);
    const id = route.params.id;

    useEffect(() => {
        if (!id) {
            navigate("APPROVALS");
        } else {
            getApproval({ variables: { id } });
        }
    }, [getApproval, navigate, id]);

    useEffect(() => {
        if (!approval.data || !approval.data.approval) {
            return;
        }

        setDefaultValues({
            type: approval.data.approval.type,
            active: approval.data.approval.active,
            tokenIds: approval.data.approval.approvalTokens.map(itr => itr.id),
            tokenGroupIds: approval.data.approval.approvalTokenGroups.map(
                itr => itr.id
            ),
        });
    }, [approval.data, setDefaultValues]);

    const handleSave = useCallback(async () => {
        if (!approval || !approval.data || !approval.data.approval || !values) {
            return;
        }

        await handleMutationError(
            async () =>
                await approvalSave({
                    variables: {
                        id: approval.data!.approval!.id,
                        approval: {
                            type: values.type,
                            active: values.active,
                            tokenIds: values.tokenIds,
                            tokenGroupIds: values.tokenGroupIds,
                        },
                    },
                }),
            t("common.saved", "Saved"),
            () => {
                navigate("APPROVALS", { refetch: true });
            }
        );
    }, [handleMutationError, navigate, approval, approvalSave, t, values]);

    return (
        <AdminContainer>
            <View>
                <View style={styles.formBox}>
                    <Form
                        form={form}
                        onSubmit={handleSave}
                        submitButton={["common.save", "Save"]}
                        isEditing={true}
                    />
                </View>
            </View>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    formBox: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
